import { FC } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Button,
  Skeleton,
} from '@mui/material';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import DoneIcon from '@mui/icons-material/Done';

const labelStyles = {
  fontSize: '1rem',
  fontWeight: 700,
  lineHeight: '1.375rem',
  color: '#162936',
};

const buttonStyles = {
  fontSize: '0.875rem',
  fontWeight: 700,
  color: '#51B8B0',
  textTransform: 'none',
  padding: 0,
  minWidth: 'auto',
  textDecoration: 'underline',
};
const ITEMS_PER_ROW = 3;

const optionLabelStyles = {
  fontSize: '0.875rem',
  fontWeight: 500,
  color: '#1B1C17',
};

export type MultiSelectAccordionMode = 'select' | 'readonly';
interface MultiSelectAccordionProps {
  mode?: MultiSelectAccordionMode;
  title: string;
  description?: string;
  isExpanded: boolean;
  selectedItems: string[];
  listItems: { label: string; value: string }[];
  onExpand: (expanded: boolean) => void;
  onSelectItem?: (items: string) => void;
  onSelectAll?: () => void;
  onClearAll?: () => void;
  isLoading?: boolean;
}
const MultiSelectAccordion: FC<MultiSelectAccordionProps> = ({
  mode = 'select',
  title,
  description,
  isExpanded,
  selectedItems,
  listItems,
  onExpand,
  onSelectItem,
  onSelectAll,
  onClearAll,
  isLoading = false,
}) => {
  const isReadonlyMode = mode === 'readonly';
  const handleSelectItem = (item: string) => {
    onSelectItem?.(item);
  };
  const handleSelectAll = () => {
    onSelectAll?.();
  };
  const handleClearAll = () => {
    onClearAll?.();
  };
  return (
    <Accordion
      expanded={isExpanded}
      onChange={(_, expanded) => onExpand(expanded)}
      elevation={0}
      disableGutters
      sx={{
        p: 0,
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary
        expandIcon={
          <ExpandMoreOutlinedIcon
            sx={{
              color: '#162C36',
              fontSize: '1.5rem',
            }}
          />
        }
        sx={{
          minHeight: 'unset',
          color: '#162936',
          p: 0,
          fontWeight: 700,
          fontSize: '0.875rem',
          '& .MuiAccordionSummary-content': { my: 0 },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography sx={labelStyles}>{title}</Typography>
          {description && (
            <Typography
              sx={{
                fontSize: '0.75rem',
                fontWeight: 500,
                color: '#6C6C6C',
                mb: '0.5rem',
              }}
            >
              {description}
            </Typography>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {!isReadonlyMode && (
          <Box sx={{ display: 'flex', gap: '0.75rem' }}>
            <Button variant="text" size="small" sx={buttonStyles} onClick={handleSelectAll}>
              Select All
            </Button>
            <Button variant="text" size="small" sx={buttonStyles} onClick={handleClearAll}>
              Clear All
            </Button>
          </Box>
        )}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${ITEMS_PER_ROW}, max-content)`,
            columnGap: '1.5rem',
            rowGap: 0,
            justifyContent: 'start',
          }}
        >
          {isLoading ? (
            <>
              <Skeleton variant="text" height={30} width={150} />
              <Skeleton variant="text" height={30} width={150} />
            </>
          ) : (
            listItems.map((item) => (
              <FormControlLabel
                key={item.value}
                disabled={isReadonlyMode}
                control={
                  isReadonlyMode ? (
                    <DoneIcon sx={{ color: '#162C36', fontSize: '1.125rem', m: '0.5rem' }} />
                  ) : (
                    <Checkbox
                      disableRipple
                      checked={selectedItems.includes(item.value)}
                      onChange={() => handleSelectItem(item.value)}
                      size="small"
                      sx={{
                        color: '#737F86',
                        '&.Mui-checked': { color: '#2F736E' },
                      }}
                    />
                  )
                }
                label={item.label}
                sx={{
                  m: 0,
                  alignItems: 'center',
                  '& .MuiFormControlLabel-label': optionLabelStyles,
                  '&.Mui-disabled .MuiFormControlLabel-label': optionLabelStyles,
                }}
              />
            ))
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default MultiSelectAccordion;
